/* eslint-disable @typescript-eslint/no-explicit-any */
import type { TableSortValue } from "@src/shared/constants/sort-order.constants";
import apiService from "./api";
import { sanitizeQueryParams } from "../utility/helpers/apiHelper";

export interface AppUser {
  id: number;
  firstName: string | null;
  lastName: string | null;
  userName?: string | null;
  dateOfBirth?: string | null;
  email: string;
  phoneNumber: string | null;
  status: string;
  role?: string | null;
  roleName?: string | null;
  isEmailVerified?: boolean | number;
  stripeCustomerId?: string | null;
  createdAt: string;
  updatedAt?: string;
  displayName?: string;
  businessUserId?: number;
  businessId?: number;
  businessName?: string | null;
  businessEmail?: string | null;
  businessPhone?: string | null;
  businessWebsite?: string | null;
  businessDescription?: string | null;
  businessLogoUrl?: string | null;
  verificationStatus?: string | null;
}

export interface LinkedBusiness {
  businessUserId: number;
  businessId: number;
  linkedAt: string;
  businessName: string;
  description?: string | null;
  email?: string | null;
  phone?: string | null;
  logoUrl?: string | null;
  website?: string | null;
  verificationStatus?: string | null;
}

export interface AppUserBooking {
  bookingId: number;
  businessId: number;
  businessName?: string | null;
  date?: string | null;
  time?: string | null;
  addOns?: number;
  locationType?: string | null;
  bookingStatus?: string | null;
  paymentStatus?: string | null;
  totalAmount?: string | number | null;
  duration?: number | null;
  createdAt?: string | null;
}

export interface AppUserStats {
  bookingsCount: number;
  postsCount: number;
  transactionsCount: number;
  totalSpend: number;
  refundsCount: number;
  returnsCount: number;
  reviewsCount: number;
  ticketsCount: number;
}

export interface AppUserTransactionSummary {
  id: number;
  transactionType: string;
  paymentMethod: string;
  status: string;
  finalAmount: number;
  currency?: string | null;
  createdAt?: string | null;
}

export interface AppUserRefundSummary {
  id: number;
  type?: string | null;
  itemName?: string | null;
  refundAmount: number;
  status: string;
  currency?: string | null;
  createdAt?: string | null;
}

export interface AppUserReturnSummary {
  id: number;
  productName?: string | null;
  status: string;
  refundAmount?: number | null;
  currency?: string | null;
  createdAt?: string | null;
}

export interface AppUserReviewSummary {
  id: number;
  entityType: string;
  entityId: number;
  rating: number;
  comment?: string | null;
  createdAt?: string | null;
}

export interface AppUserTicketSummary {
  id: number;
  title?: string | null;
  category?: string | null;
  status: string;
  priority?: string | null;
  entityType?: string | null;
  entityId?: number | null;
  createdAt?: string | null;
}

export interface UserPost {
  id?: number;
  postId?: number;
  businessId?: number | null;
  postType?: string | null;
  title?: string | null;
  content?: string | null;
  visibility?: string | null;
  tags?: string | null;
  mentions?: string | null;
  likeCount?: number;
  commentCount?: number;
  viewCount?: number;
  media?: string[];
  status?: string | null;
  createdAt?: string | null;
  [key: string]: unknown;
}

export interface AppUserDetails {
  user: AppUser;
  stats: AppUserStats;
  bookings: AppUserBooking[];
  posts: UserPost[];
  transactions: AppUserTransactionSummary[];
  refunds: AppUserRefundSummary[];
  returns: AppUserReturnSummary[];
  reviews: AppUserReviewSummary[];
  tickets: AppUserTicketSummary[];
}

export interface BusinessUserDetails {
  user: AppUser;
  businesses: LinkedBusiness[];
  posts: UserPost[];
  bookings?: StaffBooking[];
}

export interface StaffBooking {
  bookingId: number;
  businessName: string;
  date: string;
  time: string;
  bookingStatus: string;
  paymentStatus: string;
  totalAmount: string;
}

export type CustomerUserDetails = AppUserDetails | BusinessUserDetails;

export const isBusinessUserDetails = (
  data: AppUser | CustomerUserDetails
): data is BusinessUserDetails => "user" in data && "businesses" in data;

export const isAppUserDetails = (
  data: AppUser | CustomerUserDetails
): data is AppUserDetails => "user" in data && "bookings" in data;

export interface AppUserByRole {
  userId: number;
  email: string;
  role: string;
}

export interface GetAppUsersParams {
  page?: number;
  pageSize?: number;
  limit?: number;
  search?: string;
  status?: string;
  orderBy?: string;
  orderDirection?: TableSortValue;
  [key: string]: any;
}

export interface CreateAppUserBody {
  firstName: string;
  lastName: string;
  email: string;
  userName?: string;
  dateOfBirth?: string;
  phoneNumber?: string;
  password?: string;
  status?: string;
}

export interface UpdateAppUserBody {
  firstName?: string;
  lastName?: string;
  email?: string;
  userName?: string;
  dateOfBirth?: string;
  phoneNumber?: string;
  password?: string;
  status?: string;
}

const toCustomerListParams = (query: GetAppUsersParams = {}) =>
  sanitizeQueryParams({
    page: query.page,
    pageSize: query.pageSize ?? query.limit,
    search: query.search,
    status: query.status,
    orderBy: query.orderBy,
    orderDirection: query.orderDirection,
  });

export class CustomerService {
  async getAppUsers(query?: GetAppUsersParams) {
    return apiService.get("/customers/app-users", {
      params: toCustomerListParams(query),
    });
  }

  async getBusinessUsers(query?: GetAppUsersParams) {
    return apiService.get("/customers/business-users", {
      params: toCustomerListParams(query),
    });
  }

  async getBusinessUsersDetails(id: string | number) {
    return apiService.get(`/customers/business-users/${id}`);
  }

  /** Distinct roles from app_users.role */
  async getAppUserRoles() {
    return apiService.get("/customers/app-users/roles");
  }

  /** Users (userId + email) for a given app_users.role */
  async getAppUsersByRole(role: string) {
    return apiService.get("/customers/app-users/by-role", {
      params: { role },
    });
  }

  async getAppUser(id: string | number) {
    return apiService.get(`/customers/app-users/${id}`);
  }

  async createAppUser(body: CreateAppUserBody) {
    return apiService.post("/customers", body);
  }

  async updateAppUser(id: string | number, body: UpdateAppUserBody) {
    return apiService.put(`/customers/app-users/${id}`, body);
  }

  async updateAppUserStatus(id: string | number, status: string) {
    return apiService.patch(`/customers/app-users/${id}/status`, { status });
  }

  async deleteAppUser(id: string | number) {
    return apiService.delete(`/customers/app-users/${id}`);
  }
}

export const customerService = new CustomerService();
